import { JsonRpcSigner } from "@ethersproject/providers";
import { createOrUpdateTextFile } from "@octokit/plugin-create-or-update-text-file";
import { Octokit } from "@octokit/rest";
import { PERMIT2_ADDRESS } from "@uniswap/permit2-sdk";
import { ethers } from "ethers";
import { parseUnits } from "ethers/lib/utils";
import _sodium from "libsodium-wrappers";
import YAML from "yaml";
//@ts-expect-error This is taken care of by es-build
import defaultConf from "../../types/default-configuration.yml";
import { erc20Abi } from "./abis";
import { getNetworkName, NetworkIds, Tokens } from "./constants";
import { getSessionToken, renderGitHubLoginButton } from "./github-login-button";

const classes = ["error", "warn", "success"];
const inputClasses = ["input-warn", "input-error", "input-success"];
const outKey = document.getElementById("outKey") as HTMLInputElement;
const orgName = document.getElementById("orgName") as HTMLInputElement;
const walletPrivateKey = document.getElementById("walletPrivateKey") as HTMLInputElement;
const confirmButton = document.getElementById("confirmButton") as HTMLButtonElement;
const allowanceInput = document.getElementById("allowance") as HTMLInputElement;
const chainIdSelect = document.getElementById("chainId") as HTMLSelectElement;

const APP_ID = 975031;
const REPO_NAME = ".ubiquity-os";
const KEY_PATH = ".github/.ubiquity-os.config.yml";
const PRIVATE_ENCRYPTED_KEY_NAME = "evmPrivateEncrypted";
const EVM_NETWORK_KEY_NAME = "evmNetworkId";
const KEY_PREFIX = "HSK_";
// cspell:disable-next-line
const X25519_KEY = "5ghIlfGjz_ChcYlBDOG7dzmgAgBPuTahpvTMBipSH00";
const STATUS_LOG = ".status-log";

let encryptedValue = "";

export async function parseYAML<T>(data: string | undefined) {
  if (!data) return undefined;
  try {
    const parsedData = await YAML.parse(data);
    if (parsedData !== null) {
      return parsedData as T;
    } else {
      return undefined;
    }
  } catch (error) {
    return undefined;
  }
}

export async function parseJSON<T>(data: string) {
  try {
    const parsedData = await JSON.parse(data);
    return parsedData as T;
  } catch (error) {
    return undefined;
  }
}

export function stringifyYAML(value: Record<string, unknown>): string {
  return YAML.stringify(value, { defaultKeyType: "PLAIN", defaultStringType: "QUOTE_DOUBLE", lineWidth: 0 });
}

function getTextBox(text: string) {
  const strLen = text.split("\n").length * 22;
  return `${strLen > 140 ? strLen : 140}px`;
}

function classListToggle(targetElem: HTMLElement, target: "error" | "warn" | "success", inputElem?: HTMLInputElement | HTMLTextAreaElement) {
  classes.forEach((className) => targetElem.classList.remove(className));
  targetElem.classList.add(target);

  if (inputElem) {
    inputClasses.forEach((className) => inputElem.classList.remove(className));
    inputElem.classList.add(`input-${target}`);
  }
}

function statusToggle(type: "error" | "warn" | "success", message: string) {
  const statusKeyElements = document.getElementsByClassName("statusKey");
  Array.from(statusKeyElements).forEach((element) => {
    const statusKeyElement = element as HTMLElement;
    classListToggle(statusKeyElement, type);
    statusKeyElement.innerText = message;
  });
}

function focusToggle(targetElem: HTMLInputElement | HTMLTextAreaElement, type: "error" | "warn" | "success", message: string) {
  const infoElem = targetElem.parentNode?.querySelector(STATUS_LOG) as HTMLElement;
  infoElem.innerHTML = message;
  classListToggle(infoElem, type, targetElem);
  targetElem.focus();
}

function toggleLoader(state: "start" | "end") {
  if (state === "start") {
    confirmButton.disabled = true;
  } else {
    confirmButton.disabled = false;
  }
}

function singleToggle(type: "error" | "warn" | "success", message: string, focusElem?: HTMLInputElement | HTMLTextAreaElement) {
  statusToggle(type, message);

  if (focusElem) {
    focusToggle(focusElem, type, message);
  }

  toggleLoader("end");
}

async function sodiumEncryptedSeal(publicKey: string, secret: string) {
  outKey.value = "";
  encryptedValue = "";
  try {
    await _sodium.ready;
    const sodium = _sodium;

    const binkey = sodium.from_base64(publicKey, sodium.base64_variants.URLSAFE_NO_PADDING);
    const binsec = sodium.from_string(secret);
    const encBytes = sodium.crypto_box_seal(binsec, binkey);
    const output = sodium.to_base64(encBytes, sodium.base64_variants.URLSAFE_NO_PADDING);
    setEvmSettings(output, Number(chainIdSelect.value));
    outKey.value = stringifyYAML(defaultConf);
    outKey.style.height = getTextBox(outKey.value);
    outKey.disabled = false;
    encryptedValue = output;
    singleToggle("success", `Success: Key Encryption is ok.`);
  } catch (error: unknown) {
    if (error instanceof Error) {
      console.error(error);
      singleToggle("error", `Error: ${error.message}`);
    }
  }
}

async function setConfig() {
  try {
    toggleLoader("start");
    const pluginKit = Octokit.plugin(createOrUpdateTextFile);
    const octokit = new pluginKit({ auth: getSessionToken() });
    const { data: appInstallations } = await octokit.request("GET /orgs/{org}/installations", {
      org: orgName.value,
      per_page: 100,
    });
    const repositoryId = await upsertRepoID(octokit, orgName.value, REPO_NAME);
    const installations = appInstallations.installations.filter((installation) => installation.app_id === APP_ID);

    await handleInstall(octokit, orgName, repositoryId, installations, chainIdSelect);
  } catch (error) {
    if (!(error instanceof Error)) {
      return console.error(error);
    }
    console.error(error);
    singleToggle("error", `Error: ${error.message}`);
  }
}

async function handleInstall(
  octokit: Octokit,
  orgName: HTMLInputElement,
  repositoryId: number | null,
  ins: { id: number }[],
  chainIdSelect: HTMLSelectElement
) {
  if (ins.length > 0) {
    if (!repositoryId) {
      singleToggle("error", `Error: Repo initialization failed, try again later.`);
      return;
    }

    // Create a deep copy of the default config to avoid mutation
    const updatedConf = JSON.parse(JSON.stringify(defaultConf));
    setEvmSettings(encryptedValue, Number(chainIdSelect.value));

    // No need to combine since updatedConf is already a copy of defaultConf
    const combinedConf = updatedConf;

    const stringified = btoa(stringifyYAML(combinedConf));

    // Get the current file's SHA if it exists
    let sha: string | undefined;
    try {
      const { data } = await octokit.repos.getContent({
        owner: orgName.value,
        repo: REPO_NAME,
        path: KEY_PATH,
      });
      if (!Array.isArray(data)) {
        sha = data.sha;
      }
    } catch (error) {
      // File doesn't exist yet, which is fine
    }

    const { status } = await octokit.repos.createOrUpdateFileContents({
      owner: orgName.value,
      repo: REPO_NAME,
      path: KEY_PATH,
      content: stringified,
      message: `${crypto.randomUUID()}`,
      ...(sha ? { sha } : {}),
    });

    console.trace(status);

    if (status === 201 || status === 200) {
      singleToggle("success", `Success: private key is updated.`);
    } else {
      singleToggle("success", `Success: private key is up to date.`);
    }

    await nextStep();
  } else {
    singleToggle("warn", `Warn: Please install the app first.`);
  }
}

async function upsertRepoID(octokit: Octokit, orgName: string, repoName: string): Promise<number | null> {
  let repositoryId: number | null = null;

  try {
    const { data: repositoryInfo } = await octokit.rest.search.repos({
      q: `repo:${orgName}/${repoName}`,
    });
    repositoryId = repositoryInfo.items[0].id;
  } catch (error) {
    if (!(error instanceof Error)) {
      console.error(error);
      return null;
    }

    console.error(error.message);
    try {
      const { data: repoRes } = await octokit.rest.repos.createInOrg({
        org: orgName,
        name: repoName,
        auto_init: true,
        private: true,
        visibility: "private",
        has_downloads: true,
      });
      repositoryId = repoRes.id;
    } catch (error) {
      if (!(error instanceof Error)) {
        console.error(error);
        return null;
      }
      console.error(error.message);
      singleToggle("error", `Error: Repo initialization failed, try again later.`);
      return null;
    }
  }
  return repositoryId;
}

function setInputListeners() {
  const inputs = document.querySelectorAll("input") as NodeListOf<HTMLInputElement>;

  inputs.forEach((input) => {
    input.addEventListener("input", (e) => {
      inputClasses.forEach((className) => (e.target as HTMLInputElement).classList.remove(className));
    });
  });
}

let currentStep = 1;
let signer: JsonRpcSigner | undefined = undefined;

async function nextStep() {
  const configChainId = Number(chainIdSelect.value);

  const tokenNameSpan = document.getElementById("allowance + span");
  if (tokenNameSpan) {
    if (configChainId === NetworkIds.Mainnet) {
      tokenNameSpan.innerHTML = "DAI";
    } else if (configChainId === NetworkIds.Gnosis) {
      tokenNameSpan.innerHTML = "WXDAI";
    }
  }

  confirmButton.innerText = "Approve";
  currentStep = 2;

  if (!window.ethereum) {
    singleToggle("error", `Error: Please install MetaMask or any other Ethereum wallet.`);
    return;
  }

  const provider = new ethers.providers.Web3Provider(window.ethereum);
  signer = await connectWallet();
  if (!signer) {
    singleToggle("error", `Error: Please connect to MetaMask.`);
    return;
  }

  const currentChainId = await signer.getChainId();

  if (configChainId !== currentChainId) {
    singleToggle("error", `Error: Please connect to ${getNetworkName(configChainId)}.`);
    if (await switchNetwork(provider, configChainId)) {
      singleToggle("success", ``);
    }
  }

  // watch for chain changes             making this generic suppresses the unknown comparison
  window.ethereum.on("chainChanged", async <T>(currentChainId: T | string) => {
    if (configChainId === parseInt(currentChainId as string, 16)) {
      singleToggle("success", ``);
    } else {
      singleToggle("error", `Error: Please connect to ${getNetworkName(configChainId)}.`);
      switchNetwork(provider, configChainId).catch((error) => {
        console.error(error);
      });
    }
  });
}

async function connectWallet(): Promise<JsonRpcSigner | undefined> {
  try {
    const provider = new ethers.providers.Web3Provider(window.ethereum, "any");
    await provider.send("eth_requestAccounts", []);
    return provider.getSigner();
  } catch (error: unknown) {
    if (error instanceof Error) {
      if (error?.message?.includes("missing provider")) {
        singleToggle("error", "Error: Please install MetaMask.");
      } else {
        singleToggle("error", "Error: Please connect your wallet.");
      }
      return undefined;
    }
  }
}

async function switchNetwork(provider: ethers.providers.Web3Provider, chainId: string | number): Promise<boolean> {
  try {
    // if chainId is a number then convert it to hex
    if (typeof chainId === "number") {
      chainId = `0x${chainId.toString(16)}`;
    }
    // if chainId is a string but doesn't start with 0x then convert it to hex
    if (typeof chainId === "string" && !chainId.startsWith("0x")) {
      chainId = `0x${Number(chainId).toString(16)}`;
    }
    await provider.send("wallet_switchEthereumChain", [{ chainId: chainId }]);
    return true;
  } catch (error: unknown) {
    return false;
  }
}

function isHex(str: string): boolean {
  const regexp = /^[0-9a-fA-F]+$/;
  return regexp.test(str);
}

async function step1Handler() {
  if (walletPrivateKey.value === "") {
    singleToggle("warn", `Warn: Private_Key is not set.`, walletPrivateKey);
    return;
  }
  if (!isHex(walletPrivateKey.value)) {
    singleToggle("warn", `Warn: Private_Key is not a valid hex string.`, walletPrivateKey);
    return;
  }
  if (walletPrivateKey.value.length !== 64) {
    singleToggle("warn", `Warn: Private_Key must be 32 bytes long.`, walletPrivateKey);
    return;
  }
  if (orgName.value === "") {
    singleToggle("warn", `Warn: Org Name is not set.`, orgName);
    return;
  }

  const pluginKit = Octokit.plugin();
  const octokit = new pluginKit({ auth: getSessionToken() });
  const { data } = await octokit.rest.orgs.get({ org: orgName.value });
  // https://github.com/ubiquity-os-marketplace/text-conversation-rewards?tab=readme-ov-file#how-to-encrypt-the-evmprivateencrypted-parameter
  await sodiumEncryptedSeal(X25519_KEY, `${KEY_PREFIX}${walletPrivateKey.value}:${data.id}`);
  setConfig().catch((error) => {
    console.error(error);
  });
}

async function step2Handler() {
  try {
    if (!window.ethereum) {
      singleToggle("error", `Error: Please install MetaMask or any other Ethereum wallet.`);
      return;
    }

    const provider = new ethers.providers.Web3Provider(window.ethereum);

    // if wallet is still not connected then retry connecting
    if (!signer) {
      signer = await connectWallet();
      if (!signer) {
        singleToggle("error", `Error: Please connect to MetaMask.`);
        return;
      }
    }

    const walletChainId = await signer.getChainId();
    const configChainId = Number(chainIdSelect.value);

    window.ethereum.on("chainChanged", async <T>(currentChainId: T | string) => {
      if (configChainId === parseInt(currentChainId as string, 16)) {
        singleToggle("success", ``);
      } else {
        singleToggle("error", `Error: Please connect to ${chainIdSelect.value}.`);
        switchNetwork(provider, configChainId).catch((error) => {
          console.error(error);
        });
      }
    });

    if (walletChainId !== configChainId && !(await switchNetwork(provider, configChainId))) {
      singleToggle("error", `Error: Switch to the correct chain.`);
      return;
    }

    // load token contract
    let token = "";
    if (configChainId === NetworkIds.Mainnet) {
      token = Tokens.DAI;
    } else if (configChainId === NetworkIds.Gnosis) {
      token = Tokens.WXDAI;
    }
    const erc20 = new ethers.Contract(token, erc20Abi, signer);
    const decimals = await erc20.decimals();
    const allowance = Number(allowanceInput.value);
    if (allowance <= 0) {
      singleToggle("error", `Error: Allowance should be greater than 0.`);
      return;
    }

    await erc20.approve(PERMIT2_ADDRESS, parseUnits(allowance.toString(), decimals));
    singleToggle("success", `Success`);
  } catch (error: unknown) {
    if (error instanceof Error) {
      console.error(error);
      singleToggle("error", `Error: ${error.message}`);
    }
  }
}

async function populateOrgs() {
  if (getSessionToken()) {
    const pluginKit = Octokit.plugin(createOrUpdateTextFile);
    const octokit = new pluginKit({ auth: getSessionToken() });
    const { data } = await octokit.rest.orgs.listForAuthenticatedUser({ per_page: 100 });
    const selectContainer = document.getElementById("orgName");
    if (selectContainer) {
      selectContainer.innerHTML = "";
      if (data.length) {
        selectContainer.removeAttribute("disabled");
        for (const repo of data) {
          const optionElem = document.createElement("option");
          optionElem.value = repo.login;
          optionElem.innerText = repo.login;
          selectContainer.appendChild(optionElem);
        }
      }
    }
  }
}

function setEvmSettings(privateKey: string, evmNetwork: number) {
  for (const plugin of defaultConf.plugins) {
    for (const use of plugin.uses) {
      if (use.plugin.includes("text-conversation-rewards")) {
        use.with = {
          ...use.with,
          [PRIVATE_ENCRYPTED_KEY_NAME]: privateKey,
          [EVM_NETWORK_KEY_NAME]: evmNetwork,
        };
      }
    }
  }
}

async function init() {
  if (defaultConf !== undefined) {
    try {
      setInputListeners();
      await renderGitHubLoginButton();
      await populateOrgs();

      confirmButton.addEventListener("click", async () => {
        if (currentStep === 1) {
          await step1Handler();
        } else if (currentStep === 2) {
          await step2Handler();
        }
      });
    } catch (error) {
      console.error(error);
    }
  } else {
    throw new Error("Default config fetch failed");
  }
}

init().catch((error) => {
  console.error(error);
});

import { grid } from "../the-grid";
grid(document.getElementById("grid") as HTMLElement, () => document.body.classList.add("grid-loaded")); // @DEV: display grid background
